import { Form, Formik } from 'formik';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import React, { useMemo, useState } from 'react';
import { useAppSelector } from 'store/hooks';
import UDFormInput from 'ud-form/components/input';
import UDRadio from 'ud-form/components/radio';
import UDFormLabel from 'ud-form/components/label';
import UDButton from 'ud-ui/components/button';
import Text from 'ud-ui/components/text';
import * as Yup from 'yup';
import { ScrapJsonImportService } from 'modules/migration/services/scrap-json-import-service';
import JsonFileField from './json-file-field';

type ImportSource = 'file' | 'url';

interface JsonImportFormValues {
  tournamentId: number | '';
  source: ImportSource;
  jsonUrl: string;
  jsonFile: File | null;
}

const validationSchema = Yup.object().shape({
  tournamentId: Yup.number()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return undefined;
      }

      const num = Number(originalValue);
      return Number.isNaN(num) ? undefined : num;
    })
    .required('ID турнира обязателен')
    .positive('ID турнира должен быть положительным числом'),
  source: Yup.mixed<ImportSource>().oneOf(['file', 'url']).required(),
  jsonUrl: Yup.string().when('source', {
    is: 'url',
    then: (schema) => schema.trim().url('Укажите корректный URL').required('URL JSON-файла обязателен'),
    otherwise: (schema) => schema.notRequired(),
  }),
  jsonFile: Yup.mixed<File>().when('source', {
    is: 'file',
    then: (schema) => schema.required('Выберите JSON-файл'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
});

const JsonImportForm: React.FC = () => {
  const activeTournamentId = useAppSelector(authStoreUserTournamentId);
  const [importResult, setImportResult] = useState<{ success: boolean; message: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const initialValues = useMemo<JsonImportFormValues>(
    () => ({
      tournamentId: activeTournamentId ?? '',
      source: 'file',
      jsonUrl: '',
      jsonFile: null,
    }),
    [activeTournamentId],
  );

  const handleSubmit = async (values: JsonImportFormValues) => {
    if (typeof values.tournamentId !== 'number') {
      return;
    }

    setIsLoading(true);
    setImportResult(null);

    try {
      const service = new ScrapJsonImportService();
      const result = await service.importJson({
        tournamentId: values.tournamentId,
        file: values.source === 'file' ? values.jsonFile : null,
        jsonUrl: values.source === 'url' ? values.jsonUrl : undefined,
      });

      setImportResult(result);
    } catch (error: unknown) {
      setImportResult({
        success: false,
        message: error instanceof Error ? error.message : 'Неизвестная ошибка импорта',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-8 w-full border-t border-gray-200 pt-8">
      <Text type="title-3" className="mb-2">
        Импорт турнира из JSON
      </Text>
      <Text type="subhead" className="mb-4">
        Загрузите scrap JSON с командами, игроками, чемпионатами и матчами. Можно выбрать файл с компьютера или указать прямую ссылку
        на JSON.
      </Text>

      <Formik initialValues={initialValues} enableReinitialize validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ values, isValid, setFieldValue }) => (
          <Form className="flex flex-col gap-4">
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <UDFormLabel htmlFor="tournamentId">ID турнира</UDFormLabel>
                  <UDFormInput name="tournamentId" type="number" placeholder="Например, 123" />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <Text type="headline">Источник JSON</Text>

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2">
                  <UDRadio
                    name="source"
                    value="file"
                    checked={values.source === 'file'}
                    onChange={() => {
                      setFieldValue('source', 'file');
                      setImportResult(null);
                    }}
                  />
                  <span>Файл с компьютера</span>
                </label>

                <label className="flex items-center gap-2">
                  <UDRadio
                    name="source"
                    value="url"
                    checked={values.source === 'url'}
                    onChange={() => {
                      setFieldValue('source', 'url');
                      setImportResult(null);
                    }}
                  />
                  <span>URL файла</span>
                </label>
              </div>

              {values.source === 'file' ? (
                <JsonFileField onPicked={() => setImportResult(null)} />
              ) : (
                <div className="mb-3">
                  <UDFormLabel htmlFor="jsonUrl">URL JSON-файла</UDFormLabel>
                  <UDFormInput name="jsonUrl" placeholder="https://example.com/world-cup-2026-import.json" />
                </div>
              )}
            </div>

            <div className="flex justify-start">
              <UDButton type="submit" variant="primary" disabled={!isValid || isLoading} loading={isLoading}>
                Импортировать JSON
              </UDButton>
            </div>

            {importResult ? (
              <div className={`alert ${importResult.success ? 'alert-success' : 'alert-danger'} mt-3`}>{importResult.message}</div>
            ) : null}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default JsonImportForm;
